import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Tree, 
  Form, 
  Input, 
  InputNumber, 
  Button, 
  Select, 
  Space, 
  Typography, 
  Progress, 
  Statistic, 
  Row, 
  Col, 
  Modal, 
  Divider, 
  Tag, 
  Tooltip, 
  Alert, 
  DatePicker, 
  message, 
  App 
} from 'antd';
import {
  TrophyOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CalendarOutlined,
  DollarOutlined,
  AimOutlined
} from '@ant-design/icons';
import apiService from '../services/apiService';
import useSettingsStore from '../store/settingsStore';

const { Title, Text } = Typography;
const { Option } = Select;

const TargetManagement = () => {
  const { message } = App.useApp();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [targetHierarchy, setTargetHierarchy] = useState({ annual: null, quarters: [], months: [] });
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);
  const [form] = Form.useForm();
  const [stats, setStats] = useState(null);
  const [realTimeStats, setRealTimeStats] = useState(null);

  useEffect(() => {
    fetchTargetHierarchy();
    fetchTargetStats();
    setSelectedTarget(null); // Clear selected target when year changes
  }, [selectedYear]);

  // Update real-time stats when selectedTarget changes
  useEffect(() => {
    if (selectedTarget) {
      calculateRealTimeStats();
    }
  }, [selectedTarget, targetHierarchy]);

  /**
   * Fetches the target hierarchy for the selected year
   * @async
   * @function fetchTargetHierarchy
   */
  const fetchTargetHierarchy = async () => {
    if (!selectedYear || selectedYear < 2000 || selectedYear > 2100) {
      message.error('Invalid year selected');
      return;
    }
    
    setLoading(true);
    try {
      const response = await apiService.get(`/targets/hierarchy/${selectedYear}`);
      if (response.data?.success && response.data.data) {
        setTargetHierarchy(response.data.data);
        // Auto-select annual target if available and no target is currently selected
        if (response.data.data.annual && !selectedTarget) {
          setSelectedTarget(response.data.data.annual);
        }
        // Update real-time stats after hierarchy is loaded
        setTimeout(() => calculateRealTimeStats(), 100);
      } else {
        setTargetHierarchy({ annual: null, quarters: [], months: [] });
      }
    } catch (error) {
      const errorMessage = error.response?.status === 404 
        ? 'No targets found for this year'
        : 'Failed to load targets';
      message.error(errorMessage);
      setTargetHierarchy({ annual: null, quarters: [], months: [] });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetches target statistics for the selected year
   * @async
   * @function fetchTargetStats
   */
  const fetchTargetStats = async () => {
    try {
      const response = await apiService.get(`/targets/stats/${selectedYear}`);
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      // Silently handle stats fetch error
    }
  };

  /**
   * Calculates real-time statistics based on current target hierarchy
   * @function calculateRealTimeStats
   */
  const calculateRealTimeStats = () => {
    if (!targetHierarchy) {
      setRealTimeStats(null);
      return;
    }

    // Use only annual target for the blue highlighted total
    const annualTarget = targetHierarchy.annual;
    const totalTarget = annualTarget ? annualTarget.targetAmount : 0;
    const totalAchievement = annualTarget ? annualTarget.achievedAmount : 0;
    const completionPercentage = totalTarget > 0
      ? Number(((totalAchievement / totalTarget) * 100).toFixed(2))
      : 0;

    setRealTimeStats({
      totalTarget,
      totalAchievement,
      completionPercentage,
      totalTargetAmount: totalTarget,
      totalAchievedAmount: totalAchievement,
      averageCompletion: completionPercentage
    });
  };

  /**
   * Refreshes target achievements based on closed deals
   * @async
   * @function refreshAchievements
   */
  const refreshAchievements = async () => {
    if (!selectedYear) {
      message.error('Please select a valid year');
      return;
    }
    
    setLoading(true);
    try {
      await apiService.post('/targets/refresh-achievements', { year: selectedYear });
      message.success('Target achievements updated successfully');
      await Promise.all([
        fetchTargetHierarchy(),
        fetchTargetStats()
      ]);
      calculateRealTimeStats();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to refresh achievements';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Auto-creates quarterly and monthly targets when annual target is created
   * @async
   * @function createSubTargets
   * @param {number} annualAmount - Annual target amount
   * @param {string} currency - Currency code
   * @param {number} year - Target year
   */
  const createSubTargets = async (annualAmount, currency, year) => {
    const quarterlyAmount = Math.round(annualAmount / 4); // Each quarter gets annual/4
    const monthlyAmount = Math.round(annualAmount / 12); // Each month gets annual/12
    
    const subTargets = [];
    
    // Create quarterly targets
    for (let i = 1; i <= 4; i++) {
      subTargets.push({
        year,
        type: 'quarterly',
        period: `Q${i}`,
        targetAmount: quarterlyAmount,
        currency,
        description: `Auto-generated Q${i} ${year} target`
      });
    }
    
    // Create monthly targets
    for (let i = 1; i <= 12; i++) {
      const monthPeriod = String(i).padStart(2, '0');
      subTargets.push({
        year,
        type: 'monthly',
        period: monthPeriod,
        targetAmount: monthlyAmount,
        currency,
        description: `Auto-generated ${new Date(year, i-1, 1).toLocaleString('default', { month: 'long' })} ${year} target`
      });
    }
    
    // Create all sub-targets
    try {
      await Promise.all(subTargets.map(target => 
        apiService.post('/targets', target)
      ));
      message.success(`Annual target created with ${subTargets.length} sub-targets (4 quarterly + 12 monthly)`);
    } catch (error) {
      message.warning('Annual target created, but some sub-targets may have failed to create');
    }
  };

  /**
   * Handles target creation
   * @async
   * @function handleCreateTarget
   * @param {Object} values - Form values for the new target
   */
  const handleCreateTarget = async (values) => {
    // Input validation
    if (!values.targetAmount || values.targetAmount <= 0) {
      message.error('Target amount must be greater than 0');
      return;
    }
    
    if (!values.type) {
      message.error('Target type is required');
      return;
    }
    
    if (values.type !== 'annual' && !values.period) {
      message.error('Period is required for quarterly and monthly targets');
      return;
    }
    
    try {
      const payload = {
        ...values,
        year: selectedYear,
        targetAmount: Number(values.targetAmount)
      };
      
      // Remove period for annual targets
      if (values.type === 'annual') {
        delete payload.period;
      }
      
      const response = await apiService.post('/targets', payload);
      
      // If annual target is created, auto-create quarterly and monthly targets
      if (values.type === 'annual') {
        await createSubTargets(
          Number(values.targetAmount),
          values.currency || 'INR',
          selectedYear
        );
      } else {
        message.success('Target created successfully');
      }
      
      setModalVisible(false);
      form.resetFields();
      await fetchTargetHierarchy();
      await fetchTargetStats();
      calculateRealTimeStats();
    } catch (error) {
      const errorMessage = error.response?.data?.details?.[0] || 
                          error.response?.data?.message || 
                          'Failed to create target';
      message.error(errorMessage);
    }
  };

  /**
   * Handles target updates
   * @async
   * @function handleUpdateTarget
   * @param {Object} values - Updated form values
   */
  const handleUpdateTarget = async (values) => {
    // Input validation
    if (!values.targetAmount || values.targetAmount <= 0) {
      message.error('Target amount must be greater than 0');
      return;
    }
    
    if (!editingTarget?._id) {
      message.error('Invalid target selected for update');
      return;
    }
    
    try {
      await apiService.put(`/targets/${editingTarget._id}`, {
        ...values,
        targetAmount: Number(values.targetAmount)
      });
      message.success('Target updated successfully');
      setModalVisible(false);
      setEditingTarget(null);
      form.resetFields();
      await fetchTargetHierarchy();
      await fetchTargetStats();
      calculateRealTimeStats();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update target';
      message.error(errorMessage);
    }
  };

  /**
   * Handles target deletion with confirmation
   * @function handleDeleteTarget
   * @param {Object} target - Target to delete
   */
  const handleDeleteTarget = (target) => {
    Modal.confirm({
      title: 'Delete Target',
      content: `Are you sure you want to delete this ${target.type} target?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await apiService.delete(`/targets/${target._id}`);
          message.success('Target deleted successfully');
          await fetchTargetHierarchy();
          await fetchTargetStats();
          if (selectedTarget?._id === target._id) {
            setSelectedTarget(null);
          }
          calculateRealTimeStats();
        } catch (error) {
          message.error('Failed to delete target');
        }
      }
    });
  };

  /**
   * Renders a target node in the hierarchy tree
   * @function renderTargetNode
   * @param {Object} target - Target data
   * @param {number} level - Nesting level for indentation
   * @returns {JSX.Element} Target node component
   */
  const renderTargetNode = (target, level = 0) => {
    return (
      <div 
        className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded"
        onClick={() => setSelectedTarget(target)}
      >
        <div className="flex items-center">
          {target.type === 'annual' ? <TrophyOutlined className="mr-2 text-gold" /> : 
            target.type === 'quarterly' ? <CalendarOutlined className="mr-2 text-blue-500" /> : 
            <CalendarOutlined className="mr-2 text-green-500" />}
          <span>{target.type === 'annual' ? 'Annual Target' : target.period}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Text type="secondary">{formatCurrency(target.targetAmount)}</Text>
          <Progress 
            type="circle" 
            size={20} 
            percent={target.completionPercentage || 0}
            showInfo={false}
          />
        </div>
      </div>
    );
  };

  /**
   * Opens the create target modal
   * @function openCreateModal
   * @param {string} type - Target type (annual, quarterly, monthly)
   * @param {string|null} period - Target period
   */
  const openCreateModal = (type, period = null) => {
    setEditingTarget(null);
    form.resetFields();
    form.setFieldsValue({ type, period });
    setModalVisible(true);
  };

  /**
   * Opens the edit target modal
   * @function openEditModal
   * @param {Object} target - Target to edit
   */
  const openEditModal = (target) => {
    setEditingTarget(target);
    form.setFieldsValue({
      type: target.type,
      period: target.period,
      targetAmount: target.targetAmount,
      currency: target.currency,
      description: target.description
    });
    setModalVisible(true);
  };

  /**
   * Gets progress bar color based on achievement percentage
   * @function getProgressColor
   * @param {number} percentage - Achievement percentage
   * @returns {string} Color code for progress bar
   */
  const getProgressColor = (percentage) => {
    if (percentage < 50) return '#ff9900';
    if (percentage < 80) return '#87d068';
    return '#52c41a';
  };

  const generateTreeData = () => {
    if (!targetHierarchy) return [];

    const treeData = [
      {
        title: (
          <div className="flex items-center justify-between">
            <span className="font-semibold">{selectedYear}</span>
            {!targetHierarchy?.annual && (
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => openCreateModal('annual')}
              >
                Add Annual
              </Button>
            )}
          </div>
        ),
        key: `year-${selectedYear}`,
        children: []
      }
    ];

    // Annual target
    if (targetHierarchy.annual) {
      const annual = targetHierarchy.annual;
      treeData[0].children.push({
        title: renderTargetNode(annual),
        key: `annual-${annual._id}`,
        isLeaf: true
      });
    }

    // Quarterly targets
    const quarterNode = {
      title: (
        <div className="flex items-center justify-between">
          <span>Quarters</span>
          {targetHierarchy?.quarters?.length < 4 && (
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => openCreateModal('quarterly')}
            >
              Add Quarter
            </Button>
          )}
        </div>
      ),
      key: 'quarters',
      children: []
    };

    ['Q1', 'Q2', 'Q3', 'Q4'].forEach(quarter => {
      const quarterTarget = targetHierarchy.quarters.find(q => q.period === quarter);
      if (quarterTarget) {
        quarterNode.children.push({
          title: renderTargetNode(quarterTarget),
          key: `quarter-${quarterTarget._id}`,
          isLeaf: true
        });
      } else {
        quarterNode.children.push({
          title: (
            <div className="flex items-center justify-between text-gray-400">
              <div className="flex items-center">
                <CalendarOutlined className="mr-2" />
                <span>{quarter} - Not Set</span>
              </div>
            </div>
          ),
          key: `quarter-empty-${quarter}`,
          isLeaf: true
        });
      }
    });

    treeData[0].children.push(quarterNode);

    // Monthly targets
    const monthNode = {
      title: (
        <div className="flex items-center justify-between">
          <span>Months</span>
          {targetHierarchy?.months?.length < 12 && (
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => openCreateModal('monthly')}
            >
              Add Month
            </Button>
          )}
        </div>
      ),
      key: 'months',
      children: []
    };

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    monthNames.forEach((monthName, index) => {
      const monthPeriod = String(index + 1).padStart(2, '0');
      const monthTarget = targetHierarchy.months.find(m => m.period === monthPeriod);
      
      if (monthTarget) {
        monthNode.children.push({
          title: renderTargetNode(monthTarget),
          key: `month-${monthTarget._id}`,
          isLeaf: true
        });
      }
    });

    if (monthNode.children.length > 0) {
      treeData[0].children.push(monthNode);
    }

    return treeData;
  };

  // Routes through the tenant settings store: amounts stored in INR are
  // converted to the active display currency using the configured fxRate.
  const storeFormatCurrency = useSettingsStore((s) => s.formatCurrency);
  const formatCurrency = (amount) => storeFormatCurrency(amount);
  const currencySymbol = useSettingsStore((s) => s.currencySymbol);

  return (
    <App>
      <div className="h-full">
        <Row gutter={24} className="h-full">
          {/* Left Panel - Target Tree (30%) */}
          <Col span={7} className="h-full">
            <Card 
              title={
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <AimOutlined className="mr-2" />
                    <span>Targets</span>
                </div>
                <Space>
                  <Select
                    value={selectedYear}
                    onChange={(year) => {
                      setSelectedYear(year);
                      setSelectedTarget(null); // Clear selection when year changes
                    }}
                    style={{ width: 100 }}
                  >
                    {Array.from({ length: 5 }, (_, i) => {
                      const year = new Date().getFullYear() - 2 + i;
                      return (
                        <Option key={year} value={year}>
                          {year}
                        </Option>
                      );
                    })}
                  </Select>
                  <Tooltip title="Refresh Achievements">
                    <Button
                      type="primary"
                      icon={<ReloadOutlined />}
                      onClick={refreshAchievements}
                      loading={loading}
                      disabled={!targetHierarchy || (!targetHierarchy.annual && targetHierarchy.quarters?.length === 0 && targetHierarchy.months?.length === 0)}
                    />
                  </Tooltip>
                </Space>
              </div>
            }
            className="h-full"
            styles={{ body: { height: 'calc(100% - 57px)', overflow: 'auto' } }}
          >
            {(realTimeStats || stats) && (
              <Alert
                message={
                  <div className="text-center">
                    <div className="text-lg font-semibold">
                      {formatCurrency((realTimeStats || stats).totalAchievedAmount, selectedTarget?.currency || 'INR')} / {formatCurrency((realTimeStats || stats).totalTargetAmount, selectedTarget?.currency || 'INR')}
                    </div>
                    <div className="text-sm text-gray-600">
                      {((realTimeStats || stats).averageCompletion || 0).toFixed(2)}% Overall Achievement
                    </div>
                  </div>
                }
                type="info"
                showIcon={false}
                className="mb-4"
              />
            )}
            
            <Tree
              treeData={generateTreeData()}
              defaultExpandAll
              showLine
              loading={loading}
            />
          </Card>
        </Col>

        {/* Right Panel - Target Details (70%) */}
        <Col span={17} className="h-full">
          <Card 
            title={
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <DollarOutlined className="mr-2" />
                  <span>Target Details</span>
                </div>
                {selectedTarget && (
                  <Space>
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => openEditModal(selectedTarget)}
                    >
                      Edit
                    </Button>
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteTarget(selectedTarget)}
                    >
                      Delete
                    </Button>
                  </Space>
                )}
              </div>
            }
            className="h-full"
            styles={{ body: { height: 'calc(100% - 57px)', overflow: 'auto' } }}
          >
            {selectedTarget ? (
              <div className="space-y-6">
                {/* Target Overview */}
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title="Target Amount"
                      value={selectedTarget.targetAmount || 0}
                      prefix={currencySymbol}
                      formatter={(value) => formatCurrency(value, selectedTarget.currency)}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Achieved Amount"
                      value={selectedTarget.achievedAmount || 0}
                      prefix={currencySymbol}
                      formatter={(value) => formatCurrency(value, selectedTarget.currency)}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Remaining"
                      value={selectedTarget.remainingAmount || (selectedTarget.targetAmount - (selectedTarget.achievedAmount || 0))}
                      formatter={(value) => formatCurrency(value, selectedTarget.currency)}
                      valueStyle={{ color: (selectedTarget.remainingAmount || (selectedTarget.targetAmount - (selectedTarget.achievedAmount || 0))) > 0 ? '#fa8c16' : '#52c41a' }}
                    />
                  </Col>
                </Row>

                <Divider />

                {/* Progress */}
                <Row gutter={16}>
                  <Col span={24}>
                    <Statistic
                      title="Completion Rate"
                      value={selectedTarget.completionPercentage || 0}
                      precision={2}
                      suffix="%"
                      valueStyle={{ color: getProgressColor(selectedTarget.completionPercentage || 0) }}
                    />
                    <Progress
                      percent={Number(((selectedTarget.completionPercentage || 0)).toFixed(2))}
                      strokeColor={getProgressColor(selectedTarget.completionPercentage || 0)}
                      className="mt-2"
                    />
                  </Col>
                </Row>

                <Divider />

                {/* Target Information */}
                <div>
                  <Title level={5}>Target Information</Title>
                  <Row gutter={16}>
                    <Col span={12}>
                      <div className="mb-3">
                        <Text strong>Type:</Text>
                        <div>
                          <Tag color={
                            selectedTarget.type === 'annual' ? 'gold' :
                            selectedTarget.type === 'quarterly' ? 'blue' : 'green'
                          }>
                            {selectedTarget.type.charAt(0).toUpperCase() + selectedTarget.type.slice(1)}
                          </Tag>
                        </div>
                      </div>
                      {selectedTarget.period && (
                        <div className="mb-3">
                          <Text strong>Period:</Text>
                          <div>{selectedTarget.period}</div>
                        </div>
                      )}
                      <div className="mb-3">
                        <Text strong>Currency:</Text>
                        <div>{selectedTarget.currency}</div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className="mb-3">
                        <Text strong>Created:</Text>
                        <div>{new Date(selectedTarget.createdAt).toLocaleDateString()}</div>
                      </div>
                      {selectedTarget.updatedAt !== selectedTarget.createdAt && (
                        <div className="mb-3">
                          <Text strong>Last Updated:</Text>
                          <div>{new Date(selectedTarget.updatedAt).toLocaleDateString()}</div>
                        </div>
                      )}
                      <div className="mb-3">
                        <Text strong>Status:</Text>
                        <div>
                          <Tag color={selectedTarget.isActive ? 'green' : 'red'}>
                            {selectedTarget.isActive ? 'Active' : 'Inactive'}
                          </Tag>
                        </div>
                      </div>
                    </Col>
                  </Row>
                  
                  {selectedTarget.description && (
                    <div className="mt-4">
                      <Text strong>Description:</Text>
                      <div className="mt-1 p-3 bg-gray-50 rounded">
                        {selectedTarget.description}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="text-6xl text-gray-300 mb-4">🎯</div>
                <Title level={4} type="secondary">Select a target to view details</Title>
                <Text type="secondary">
                  Choose a target from the tree on the left to see detailed information and progress
                </Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Create/Edit Target Modal */}
      <Modal
        title={editingTarget ? 'Edit Target' : 'Create New Target'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingTarget(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={editingTarget ? handleUpdateTarget : handleCreateTarget}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Target Type"
                rules={[{ required: true, message: 'Please select target type' }]}
              >
                <Select placeholder="Select type" disabled={!!editingTarget}>
                  <Option value="annual">Annual</Option>
                  <Option value="quarterly">Quarterly</Option>
                  <Option value="monthly">Monthly</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="period"
                label="Period"
                dependencies={['type']}
                rules={[
                  ({ getFieldValue }) => ({
                    required: getFieldValue('type') !== 'annual',
                    message: 'Please select period'
                  })
                ]}
              >
                <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type}>
                  {({ getFieldValue }) => {
                    const type = getFieldValue('type');
                    if (type === 'quarterly') {
                      return (
                        <Select placeholder="Select quarter" disabled={!!editingTarget}>
                          <Option value="Q1">Q1</Option>
                          <Option value="Q2">Q2</Option>
                          <Option value="Q3">Q3</Option>
                          <Option value="Q4">Q4</Option>
                        </Select>
                      );
                    } else if (type === 'monthly') {
                      return (
                        <Select placeholder="Select month" disabled={!!editingTarget}>
                          {Array.from({ length: 12 }, (_, i) => {
                            const month = String(i + 1).padStart(2, '0');
                            const monthName = new Date(2024, i, 1).toLocaleString('default', { month: 'long' });
                            return (
                              <Option key={month} value={month}>
                                {monthName}
                              </Option>
                            );
                          })}
                        </Select>
                      );
                    }
                    return <Input disabled placeholder="Not applicable for annual targets" />;
                  }}
                </Form.Item>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="targetAmount"
                label={`Target Amount (${currencySymbol})`}
                tooltip="Captured in INR. Display currency is configured in Settings → Regional."
                rules={[
                  { required: true, message: 'Please enter target amount' },
                  { type: 'number', min: 0, message: 'Amount must be positive' }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={(value) => `${currencySymbol} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value.replace(new RegExp(`${currencySymbol}\\s?|(,*)`, 'g'), '')}
                  placeholder="Enter target amount"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter target description (optional)"
            />
          </Form.Item>

          <div className="flex justify-end space-x-2">
            <Button onClick={() => {
              setModalVisible(false);
              setEditingTarget(null);
              form.resetFields();
            }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              {editingTarget ? 'Update Target' : 'Create Target'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
    </App>
  );
};

export default TargetManagement;
